import { BadgeProps } from "@radix-ui/themes";
import { GetOrderResponseType, TrackingItemType } from "../../api";
import {
  deliveryFilterState,
  getBadgeText,
  getInfoText,
  getSimpleStep,
  getSimpleText,
} from "./deliveryState";

export type ExpandedTrackingItemType = TrackingItemType & {
  simpleState: string;
  infoText: string;
  badgeText: string;
  step: number;
  level: number;
  displayDeliveryDate?: string;
};

export type MappedTrackingItemsType = Array<ExpandedTrackingItemType>;

export const getDisplayDeliveryDate = (
  dateExpected?: string,
  requestedDeliveryDate?: string,
): string | undefined => {
  const expectedTime = dateExpected ? new Date(dateExpected).getTime() : NaN;
  const requestedTime = requestedDeliveryDate
    ? new Date(requestedDeliveryDate).getTime()
    : NaN;

  const expectedValid = !isNaN(expectedTime);
  const requestedValid = !isNaN(requestedTime);

  if (expectedValid && requestedValid) {
    return requestedTime > expectedTime ? requestedDeliveryDate : dateExpected;
  }

  if (expectedValid) {
    return dateExpected;
  }

  if (requestedValid) {
    return requestedDeliveryDate;
  }

  return undefined;
};

export const trackingItemsMapper = (
  items: GetOrderResponseType["orderPos"],
  shippingMethodID?: number,
  level = 0,
): MappedTrackingItemsType => {
  const mappedItems: MappedTrackingItemsType = [];
  const sortedItems = items
    ? [...items].sort((a, b) => a.itemID - b.itemID)
    : [];
  sortedItems.forEach((item) => {
    const displayDeliveryDate = getDisplayDeliveryDate(
      item.dateExpected,
      item.requestedDeliveryDate,
    );
    const simpleState = getSimpleText(item.deliveryStatus, shippingMethodID);
    const infoText = getInfoText(item, displayDeliveryDate);
    const step = getSimpleStep(item.deliveryStatus);
    const badgeText = getBadgeText(item.deliveryStatus);
    const backorderPos = item.backorderPos
      ? trackingItemsMapper(item.backorderPos, shippingMethodID, level + 1)
      : [];
    mappedItems.push({
      ...item,
      simpleState,
      infoText,
      badgeText,
      step,
      level,
      displayDeliveryDate,
    });
    mappedItems.push(...backorderPos);
  });
  return mappedItems;
};

export const filterTrackingItems = (
  items: MappedTrackingItemsType,
  filter?: {
    searchFilter?: string;
    badgeFilter?: string;
  },
): MappedTrackingItemsType => {
  if (!filter) {
    return items;
  }
  return items.filter((item) => {
    const searchValues =
      `${item.articleName}${item.articleNo}${item.itemID}`.toLowerCase();
    if (
      filter.searchFilter &&
      !searchValues.includes(filter.searchFilter.toLowerCase())
    ) {
      return false;
    }
    if (filter.badgeFilter && item.badgeText !== filter.badgeFilter) {
      return false;
    }
    return true;
  });
};

export const getBadgeColor = (badgeText: string): BadgeProps["color"] => {
  const filterItem = Object.values(deliveryFilterState).find(
    (item) => item.text === badgeText,
  );
  return filterItem?.color || "gray";
};
